import { useEffect, useRef } from "react"

import { mtLocation } from "@/lib/mt-location"
import { useRouter } from "@/lib/router"
import {
  ProfileTabCollections,
  ProfileTabGuides,
  ProfileTabReviews,
} from "@/lib/storage/profile-slice"

type AutoSwitchProfileTab =
  | typeof ProfileTabCollections
  | typeof ProfileTabReviews
  | typeof ProfileTabGuides

interface UseProfileTabAutoSwitchParams {
  tab: AutoSwitchProfileTab
  collectionsCount: number | null | undefined
  reviewsCount: number | null | undefined
  guidesCount: number | null | undefined
  onTabChange: (tab: AutoSwitchProfileTab) => void
  enabled?: boolean
}

function getAutoSwitchTab({
  tab,
  collectionsCount,
  reviewsCount,
  guidesCount,
}: {
  tab: AutoSwitchProfileTab
  collectionsCount: number
  reviewsCount: number
  guidesCount: number
}): AutoSwitchProfileTab | null {
  let target: AutoSwitchProfileTab = ProfileTabGuides
  if (guidesCount > 0) {
    target = ProfileTabGuides
  } else if (collectionsCount > 0) {
    target = ProfileTabCollections
  } else if (reviewsCount > 0) {
    target = ProfileTabReviews
  }

  return target === tab ? null : target
}

/**
 * Writes the auto-switched tab onto the current history entry.
 *
 * The switch corrects the tab the user landed on rather than navigating
 * somewhere new, so it must not add a history entry. Letting
 * client-route-handler push one would make Back return to this same profile at
 * its (empty) default tab instead of the page the user came from. Replacing the
 * entry in place also leaves the URL matching Redux, so the route handler sees
 * no pending change and skips the push.
 */
function replaceTabInCurrentHistoryEntry(tab: AutoSwitchProfileTab) {
  const params = new URLSearchParams(mtLocation.search)
  params.set("tab", tab)
  const url = `${mtLocation.pathname}?${params.toString()}`

  window.history.replaceState(
    { ...window.history.state, as: url, url },
    "",
    url
  )
}

export function useProfileTabAutoSwitch({
  tab,
  collectionsCount,
  reviewsCount,
  guidesCount,
  onTabChange,
  enabled = true,
}: UseProfileTabAutoSwitchParams) {
  const router = useRouter()
  const hasTabQueryParam = router.query.tab != null
  const didDecide = useRef(false)

  useEffect(() => {
    if (!enabled || hasTabQueryParam || didDecide.current) return
    if (
      collectionsCount == null ||
      reviewsCount == null ||
      guidesCount == null
    ) {
      return
    }

    didDecide.current = true

    const nextTab = getAutoSwitchTab({
      tab,
      collectionsCount,
      reviewsCount,
      guidesCount,
    })

    if (nextTab) {
      replaceTabInCurrentHistoryEntry(nextTab)
      onTabChange(nextTab)
    }
  }, [
    collectionsCount,
    enabled,
    guidesCount,
    hasTabQueryParam,
    onTabChange,
    reviewsCount,
    tab,
  ])
}
